'use client';

import { useState, useEffect } from 'react';
import { errorEmitter } from '@/firebase/error-emitter';
import { FirestorePermissionError } from '@/firebase/errors';

/**
 * An invisible component that listens for globally emitted 'permission-error' events.
 *
 * In DEVELOPMENT it throws the error, so a listener denied by the rules lands in
 * the error overlay with the simulated request attached, which is what makes a
 * rules mistake quick to find.
 *
 * In PRODUCTION it only logs. This component is mounted at the root, so a throw
 * here replaced the whole site with global-error over one failed listener (a
 * search, a seller-registration read), while useCollection and useDoc already
 * hand that same error to the component that owns the listener, which can
 * render around it.
 */
export function FirebaseErrorListener() {
  // Use the specific error type for the state for type safety.
  const [error, setError] = useState<FirestorePermissionError | null>(null);

  useEffect(() => {
    // The callback now expects a strongly-typed error, matching the event payload.
    const handleError = (error: FirestorePermissionError) => {
      if (process.env.NODE_ENV === 'production') {
        console.error('Firestore listener denied by security rules:', error);
        return;
      }
      // Set error in state to trigger a re-render.
      setError(error);
    };

    // The typed emitter will enforce that the callback for 'permission-error'
    // matches the expected payload type (FirestorePermissionError).
    errorEmitter.on('permission-error', handleError);

    // Unsubscribe on unmount to prevent memory leaks.
    return () => {
      errorEmitter.off('permission-error', handleError);
    };
  }, []);

  // On re-render, if an error exists in state, throw it (development only:
  // production never sets it).
  if (error) {
    throw error;
  }

  // This component renders nothing.
  return null;
}
